import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PatientService } from 'app/core/services/patient.service';
import { PatientAttachmentsService } from 'app/core/services/patient-attachments.service';
import { ProviderService } from '../../../core/services/provider.service';
import { OfficeService } from 'app/core/services/office.service';
import { Patient } from 'app/core/models/patient.model';
import { PatientAttachment } from 'app/core/models/patient-attachment.model';
import { PatientAttachmentListing } from 'app/core/models/patient-attachment-listing.model';
import { ProviderOffice } from '../../../core/models/provider-office.model';
import { Office } from 'app/core/models/office.model';
import { switchMap, finalize } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PdfFileType } from '../../../core/enums/pdf-file-type.enum';
import { keyedStatusOptions as keyedOfficeStatusOptions } from 'app/core/options/office-status.opts';
import { keyedStatusOptions as keyedProviderOfficeStatusOptions } from '../../../core/options/provider-office-status.opts';
import { NgClass, NgTemplateOutlet, DatePipe } from '@angular/common';
import { PatientHeaderComponent } from '../../components/patient-header/patient-header.component';
import { PatientAttachmentDuplicateComponent } from '../../../shared/components/patient-attachment-duplicate/patient-attachment-duplicate.component';
import { PatientAttachmentDuplicateLinkComponent } from '../../../shared/components/patient-attachment-duplicate-link/patient-attachment-duplicate-link.component';
import { OfficeComponent } from '../../../shared/components/office/office.component';
import { PdfFileViewerComponent } from '../../../shared/components/pdf-file-viewer/pdf-file-viewer.component';
import { PdfkitFileViewerComponent } from '../../../shared/components/pdfkit-file-viewer/pdfkit-file-viewer.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PatientAttachmentListingDisplayNamePipe } from 'app/shared/pipes/patient-attachment-listing-display-name.pipe';

@Component({
    selector: 'app-capture-admin-patient-documents',
    templateUrl: './capture-admin-patient-documents.component.html',
    styleUrls: ['./capture-admin-patient-documents.component.scss'],
    imports: [PatientHeaderComponent,
              NgClass,
              PatientAttachmentDuplicateComponent,
              RouterLink,
              PatientAttachmentDuplicateLinkComponent,
              NgTemplateOutlet,
              OfficeComponent,
              PdfFileViewerComponent,
              PdfkitFileViewerComponent,
              MatProgressSpinner,
              DatePipe,
              PatientAttachmentListingDisplayNamePipe]
})
export class CaptureAdminPatientDocumentsComponent implements OnInit {
  private patientService = inject(PatientService);
  private patientAttachmentService = inject(PatientAttachmentsService);
  private officeService = inject(OfficeService);
  private providerService = inject(ProviderService);
  private route = inject(ActivatedRoute);

  patient: Patient;
  patientDocuments: PatientAttachmentListing[];
  loading: boolean;
  loadingDocument: boolean;
  selectedDocument: PatientAttachmentListing;
  currentDocument: PatientAttachment;
  outreachAttemptEvidenceOffices: Office[] = [];
  usePdfKitViewer: boolean;
  pdfFileType: PdfFileType = PdfFileType.patientAttachment;
  providerOffices: ProviderOffice[];
  providerOfficeFaxStatuses: { [key: number]: string } = {};
  tables = [];
  keyedOfficeStatusOptions = keyedOfficeStatusOptions;
  keyedProviderOfficeStatusOptions = keyedProviderOfficeStatusOptions;

  ngOnInit() {
    this.usePdfKitViewer = environment.featureData?.usePdfKitViewer
    this.init();
  }

  displayDocument(id: number): void {
    this.selectedDocument = this.patientDocuments.find(document => document.id === id);
    this.providerOfficeFaxStatuses = {};
    this.loadingDocument = true;
    this.loadDocument(id, this.selectedDocument.type);
  }

  private buildTables() {
    this.tables = [
      { title: 'Consult Notes', documents: this.documentsByType('StandardConsultNote') },
      { title: 'EHR References', documents: this.documentsByType('EhrConsultNoteReference') },
      { title: 'Outreach Attempt Evidence', documents: this.documentsByType('OutreachAttemptEvidence') }
    ]
  }

  private documentsByType = (type: string) => {
    let documents = this.patientDocuments?.filter(document => document.type === type) || []

    if (type === 'StandardConsultNote') {
      documents = this.partitionStandardConsultNotes(documents)
    }

    return documents;
  }

  private partitionStandardConsultNotes(documents: PatientAttachmentListing[]): PatientAttachmentListing[] {
    const sortedDocuments = documents.reduce((acc, doc) => {
      if (doc.displayName.startsWith('VALID')) {
        acc.valid.push(doc);
      } else if (doc.displayName.startsWith('INVALID')) {
        acc.invalid.push(doc);
      }
      return acc;
    }, { valid: [], invalid: [] });
    return [...sortedDocuments.valid, ...sortedDocuments.invalid];
  }

  private init() {
    const patientId = parseInt(this.route.snapshot.paramMap.get('id'), 10);
    this.loading = true;

    this.patientService.get(patientId).pipe(
      switchMap(patient => {
        this.patient = patient;
        return this.patientService.getDocuments(patient.id)
      }),
      finalize(() => this.loading = false)
    ).subscribe((result: PatientAttachmentListing[]) => {
      this.patientDocuments = result
      this.buildTables();
    },
      error => {
        console.error('Error fetching data', error);
        this.loading = false;
      }
    );
  }

  onUrlChange(url: string) {
    this.currentDocument.url = url;
  }

  private loadDocument(id: number, type: string) {
    this.patientAttachmentService.get(id).subscribe((patient_attachment: PatientAttachment) => {
      this.currentDocument = patient_attachment;

      if (type === 'OutreachAttemptEvidence' && patient_attachment.provider) {
        this.loadOutreachAttemptSupplementaryData(patient_attachment);
      } else {
        this.loadingDocument = false;
      }
    })
  }

  private loadOutreachAttemptSupplementaryData(patient_attachment: PatientAttachment) {
    const officeIds = patient_attachment.outreachAttemptEvidence.officeIds;
    this.loadOutreachAttemptEvidenceOffices(officeIds).subscribe((results) => {
      this.outreachAttemptEvidenceOffices = results
      this.loadProviderOffices(patient_attachment.provider.id)
    }, (error) => {
      this.loadingDocument = false;
      console.error('Error loading supplementary data:', error);
    });
  }

  private loadOutreachAttemptEvidenceOffices(officeIds: number[]): Observable<any[]> {
    const officeObservables: Observable<any>[] = officeIds.map((id) =>
      this.officeService.get(id.toString())
    );
    return forkJoin(officeObservables);
  }

  private loadProviderOffices(providerId: number) {
    this.providerService.getProviderOffices(providerId).subscribe(
      (results: ProviderOffice[]) => {
        this.buildProviderOfficeFaxStatuses(results)
      }, (error) => {
        console.error('Error loading provider offices:', error);
      });
    this.loadingDocument = false;
  }

  private buildProviderOfficeFaxStatuses(providerOffices: ProviderOffice[]): void {
    this.outreachAttemptEvidenceOffices.forEach((outreachOffice) => {
      const providerOffice = providerOffices?.find(po => po.office.id === outreachOffice.id);
      this.providerOfficeFaxStatuses[outreachOffice.id] =
        keyedProviderOfficeStatusOptions[providerOffice?.status]?.display ||
        providerOffice?.status ||
        "-"
    })
  }
}
